import {
  getLatLngFromRenderState,
  ITrackingRenderState,
  NO_VALUE_DISPLAY,
  TrackingSegmentStyleEnum,
} from "@ryde/shared";

import {
  ITrailPointFeature,
  ITrailPointFeatureCollection,
  TTrackingSpeed,
  TTrackingTimestamp,
} from "./maplibre-tracking-trail-points.models";

/** Value of `pointDetour` marking a GPS sample the vehicle recorded while off its planned route. */
const DETOUR_FLAG = 1;

/** A provider reports "no speed" as a negative value rather than omitting the field. */
const isSpeedReported = (speed: TTrackingSpeed): speed is number =>
  speed !== undefined && speed !== null && speed >= 0;

/**
 * A dot borders a no-signal gap when the trail segment on EITHER side of it is the >2-min dashed
 * (interpolated) style — the same rule the Leaflet hover note uses. Reading the two sides as named values
 * keeps the index bounds checks from burying what the test actually asks.
 */
export const isTrailPointBesideGap = (
  state: ITrackingRenderState,
  pointIndex: number,
): boolean => {
  const { segmentStyles } = state;
  const hasSegmentBefore = pointIndex > 0;
  const hasSegmentAfter = pointIndex < segmentStyles.length;
  const isGapBefore =
    hasSegmentBefore &&
    segmentStyles[pointIndex - 1] === TrackingSegmentStyleEnum.Dashed;
  const isGapAfter =
    hasSegmentAfter &&
    segmentStyles[pointIndex] === TrackingSegmentStyleEnum.Dashed;

  return isGapBefore || isGapAfter;
};

/** One trail dot, built from the packed render state at `pointIndex`. */
export const buildTrailPointFeature = ({
  state,
  pointIndex,
  bearing,
  formatTime,
  formatSpeed,
  gapNote,
}: {
  state: ITrackingRenderState;
  pointIndex: number;
  bearing: number;
  formatTime: (timestamp: TTrackingTimestamp) => string;
  formatSpeed: (speed: number) => string;
  /** Label shown when the dot borders a gap; the caller owns the translation. */
  gapNote: string;
}): ITrailPointFeature => {
  const [lat, lng] = getLatLngFromRenderState(state, pointIndex);
  const speed = state.speeds[pointIndex];

  return {
    type: "Feature",
    properties: {
      isDetour: state.pointDetour[pointIndex] === DETOUR_FLAG ? 1 : 0,
      bearing,
      time: formatTime(state.timestamps[pointIndex]),
      speed: isSpeedReported(speed) ? formatSpeed(speed) : NO_VALUE_DISPLAY,
      gapNote: isTrailPointBesideGap(state, pointIndex) ? gapNote : "",
    },
    geometry: {
      type: "Point",
      coordinates: [lng, lat],
    },
  };
};

/** The dots the layer draws: one feature per decimated point index. */
export const buildTrailPointsFeatureCollection = ({
  state,
  markerIndices,
  bearings,
  formatTime,
  formatSpeed,
  gapNote,
}: {
  state: ITrackingRenderState;
  markerIndices: readonly number[];
  bearings: number[];
  formatTime: (timestamp: TTrackingTimestamp) => string;
  formatSpeed: (speed: number) => string;
  gapNote: string;
}): ITrailPointFeatureCollection => ({
  type: "FeatureCollection",
  features: markerIndices.map(pointIndex =>
    buildTrailPointFeature({
      state,
      pointIndex,
      bearing: bearings[pointIndex],
      formatTime,
      formatSpeed,
      gapNote,
    }),
  ),
});
